import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ProductCard from './components/ProductCard';
import FilterPanel from './components/FilterPanel';
import FeaturedOffers from './components/FeaturedOffers';
import RecentlyViewed from './components/RecentlyViewed';
import PersonalizedRecommendations from './components/PersonalizedRecommendations';
import QuickViewModal from './components/QuickViewModal';

const WellnessMarketplace = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    categories: [],
    centers: [],
    priceRange: [0, 500],
    availability: [],
    minRating: null,
    search: ''
  });

  // Mock user data
  const user = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    tier: "All Access",
    goals: ["Weight Management", "Stress Relief", "Better Sleep"],
    location: "San Francisco, CA"
  };

  // Mock notifications
  const notifications = [
    {
      id: 1,
      title: "New Product Alert",
      message: "Mindfulness meditation course now available",
      time: "2 hours ago",
      read: false
    },
    {
      id: 2,
      title: "Tier Upgrade Available",
      message: "You\'re eligible for Premium membership",
      time: "1 day ago",
      read: false
    }
  ];

  // Mock categories
  const categories = [
    { id: 'fitness', name: 'Fitness & Exercise', count: 45 },
    { id: 'nutrition', name: 'Nutrition & Diet', count: 32 },
    { id: 'mental-health', name: 'Mental Health', count: 28 },
    { id: 'spa', name: 'Spa & Relaxation', count: 22 },
    { id: 'supplements', name: 'Supplements', count: 18 },
    { id: 'equipment', name: 'Equipment', count: 15 }
  ];

  // Mock wellness centers
  const centers = [
    { id: 'zen-wellness', name: 'Zen Wellness Center', productCount: 25 },
    { id: 'vitality-hub', name: 'Vitality Hub', productCount: 20 },
    { id: 'harmony-spa', name: 'Harmony Spa & Wellness', productCount: 18 },
    { id: 'peak-performance', name: 'Peak Performance Center', productCount: 15 },
    { id: 'mindful-living', name: 'Mindful Living Studio', productCount: 12 }
  ];

  // Mock featured offers
  const featuredOffers = [
    {
      id: 1,
      type: "Introductory Package",
      title: "Complete Wellness Starter Kit",
      description: "Everything you need to begin your wellness journey",
      price: 149,
      originalPrice: 199,
      discount: 25,
      validUntil: "Dec 31",
      centerName: "Zen Wellness Center",
      requiredTier: "Intro",
      icon: "Package",
      gradient: "from-primary to-secondary"
    },
    {
      id: 2,
      type: "Seasonal Special",
      title: "Winter Wellness Retreat",
      description: "3-day intensive wellness program with accommodation",
      price: 299,
      originalPrice: 399,
      discount: 25,
      validUntil: "Jan 15",
      centerName: "Harmony Spa & Wellness",
      requiredTier: "All Access",
      icon: "Snowflake",
      gradient: "from-blue-500 to-purple-600"
    },
    {
      id: 3,
      type: "Premium Exclusive",
      title: "Personal Wellness Coach",
      description: "One-on-one coaching sessions with certified experts",
      price: 199,
      originalPrice: 299,
      discount: 33,
      validUntil: "Ongoing",
      centerName: "Peak Performance Center",
      requiredTier: "Premium",
      icon: "Users",
      gradient: "from-accent to-orange-500"
    }
  ];

  // Mock products
  const products = [
    {
      id: 1,
      name: "Mindfulness Meditation Course",
      description: "8-week comprehensive meditation program designed to reduce stress and improve mental clarity",
      price: 89.99,
      originalPrice: 119.99,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400",
      category: "Mental Health",
      centerName: "Zen Wellness Center",
      centerLocation: "Downtown SF",
      centerRating: 4.8,
      centerReviews: 245,
      rating: 4.7,
      reviewCount: 128,
      availability: "available",
      isFeatured: true,
      isNew: false,
      isWishlisted: false,
      features: [
        "8 guided meditation sessions",
        "Downloadable audio content",
        "Progress tracking tools",
        "Community support access"
      ],
      images: [
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400",
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400"
      ]
    },
    {
      id: 2,
      name: "Organic Protein Powder",
      description: "Plant-based protein supplement with natural ingredients for optimal nutrition",
      price: 45.99,
      originalPrice: 59.99,
      image: "https://images.unsplash.com/photo-1593095948071-474c5cc2989d?w=400",
      category: "Supplements",
      centerName: "Vitality Hub",
      centerLocation: "Mission District",
      centerRating: 4.6,
      centerReviews: 189,
      rating: 4.5,
      reviewCount: 89,
      availability: "available",
      isFeatured: false,
      isNew: true,
      isWishlisted: true,
      features: [
        "25g plant-based protein per serving",
        "No artificial additives",
        "Gluten-free and vegan",
        "Available in 3 flavors"
      ]
    },
    {
      id: 3,
      name: "Yoga Mat Premium",
      description: "High-quality non-slip yoga mat perfect for all types of yoga practice",
      price: 79.99,
      image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400",
      category: "Equipment",
      centerName: "Mindful Living Studio",
      centerLocation: "Castro District",
      centerRating: 4.9,
      centerReviews: 156,
      rating: 4.8,
      reviewCount: 203,
      availability: "limited",
      isFeatured: false,
      isNew: false,
      isWishlisted: false,
      features: [
        "6mm thick for comfort",
        "Non-slip surface",
        "Eco-friendly materials",
        "Includes carrying strap"
      ]
    },
    {
      id: 4,
      name: "Aromatherapy Diffuser Set",
      description: "Complete aromatherapy kit with essential oils for relaxation and wellness",
      price: 129.99,
      originalPrice: 159.99,
      image: "https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=400",
      category: "Spa & Relaxation",
      centerName: "Harmony Spa & Wellness",
      centerLocation: "Nob Hill",
      centerRating: 4.7,
      centerReviews: 198,
      rating: 4.6,
      reviewCount: 156,
      availability: "available",
      isFeatured: true,
      isNew: false,
      isWishlisted: false,
      features: [
        "Ultrasonic diffuser technology",
        "6 premium essential oils included",
        "Timer and auto-shutoff",
        "LED mood lighting"
      ]
    },
    {
      id: 5,
      name: "Nutrition Consultation Package",
      description: "Personalized nutrition plan with certified dietitian consultation",
      price: 199.99,
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400",
      category: "Nutrition & Diet",
      centerName: "Peak Performance Center",
      centerLocation: "SOMA",
      centerRating: 4.8,
      centerReviews: 167,
      rating: 4.9,
      reviewCount: 94,
      availability: "available",
      isFeatured: false,
      isNew: true,
      isWishlisted: false,
      features: [
        "Initial 90-minute consultation",
        "Customized meal plans",
        "3 follow-up sessions",
        "Recipe database access"
      ]
    },
    {
      id: 6,
      name: "Fitness Tracker Pro",
      description: "Advanced fitness tracking device with heart rate monitoring and sleep analysis",
      price: 249.99,
      originalPrice: 299.99,
      image: "https://images.unsplash.com/photo-1575311373937-040b8e1fd5b6?w=400",
      category: "Equipment",
      centerName: "Vitality Hub",
      centerLocation: "Mission District",
      centerRating: 4.6,
      centerReviews: 189,
      rating: 4.4,
      reviewCount: 267,
      availability: "available",
      isFeatured: false,
      isNew: false,
      isWishlisted: true,
      features: [
        "24/7 heart rate monitoring",
        "Sleep quality analysis",
        "GPS tracking",
        "7-day battery life"
      ]
    }
  ];

  // Mock recently viewed products
  const recentlyViewed = [
    {
      id: 7,
      name: "Stress Relief Tea Blend",
      price: 24.99,
      image: "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400",
      category: "Nutrition & Diet",
      centerName: "Zen Wellness Center",
      rating: 4.3,
      viewedAt: "2 hours ago",
      isNew: false
    },
    {
      id: 8,
      name: "Meditation Cushion",
      price: 59.99,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400",
      category: "Equipment",
      centerName: "Mindful Living Studio",
      rating: 4.6,
      viewedAt: "Yesterday",
      isNew: true
    }
  ];

  // Mock personalized recommendations
  const recommendations = [
    {
      id: 9,
      name: "Weight Management Program",
      description: "Comprehensive 12-week program combining nutrition and exercise",
      price: 299.99,
      originalPrice: 399.99,
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400",
      category: "Fitness & Exercise",
      centerName: "Peak Performance Center",
      rating: 4.8,
      reviewCount: 156,
      matchScore: 95,
      recommendationReason: "Based on your Weight Management goal"
    },
    {
      id: 10,
      name: "Sleep Optimization Kit",
      description: "Complete sleep improvement package with tracking and guidance",
      price: 149.99,
      image: "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=400",
      category: "Mental Health",
      centerName: "Harmony Spa & Wellness",
      rating: 4.7,
      reviewCount: 89,
      matchScore: 92,
      recommendationReason: "Based on your Better Sleep goal"
    }
  ];

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      categories: [],
      centers: [],
      priceRange: [0, 500],
      availability: [],
      minRating: null,
      search: ''
    });
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleAddToCart = (product) => {
    console.log('Added to cart:', product);
    // Handle add to cart logic
  };

  const handleAddToWishlist = (productId, isWishlisted) => {
    console.log('Wishlist updated:', productId, isWishlisted);
    // Handle wishlist logic
  };

  const handleOfferClick = (offer) => {
    console.log('Offer clicked:', offer);
    // Handle offer click logic
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleProfileClick = () => {
    console.log('Profile clicked');
  };

  const filteredProducts = products?.filter(product => {
    // Apply filters
    if (filters?.categories?.length > 0 && !filters?.categories?.includes(product?.category?.toLowerCase()?.replace(/\s+/g, '-'))) {
      return false;
    }
    if (filters?.search && !product?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.minRating && product?.rating < filters?.minRating) {
      return false;
    }
    if (filters?.availability?.length > 0 && !filters?.availability?.includes(product?.availability)) {
      return false;
    }
    if (product?.price < filters?.priceRange?.[0] || product?.price > filters?.priceRange?.[1]) {
      return false;
    }
    return true;
  });

  const sortedProducts = [...filteredProducts]?.sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a?.price - b?.price;
      case 'price-high':
        return b?.price - a?.price;
      case 'rating':
        return b?.rating - a?.rating;
      case 'newest':
        return b?.isNew - a?.isNew;
      default:
        return b?.isFeatured - a?.isFeatured;
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onProfileClick={handleProfileClick}
      />
      <div className="flex">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          user={user}
        />

        <div className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        }`}>
          <div className="flex">
            {/* Filter Panel */}
            <FilterPanel
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
              isCollapsed={isFilterCollapsed}
              onToggle={() => setIsFilterCollapsed(!isFilterCollapsed)}
              categories={categories}
              centers={centers}
              userTier={user?.tier}
            />

            {/* Main Content */}
            <div className={`flex-1 transition-all duration-300 ${
              isFilterCollapsed ? 'ml-12' : 'ml-80'
            }`}>
              <div className="p-6">
                {/* Page Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
                        Wellness Marketplace
                      </h1>
                      <p className="text-muted-foreground">
                        Discover curated wellness products and services from trusted partners
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {/* View Mode Toggle */}
                      <div className="flex items-center bg-muted rounded-lg p-1">
                        <button
                          onClick={() => setViewMode('grid')}
                          className={`p-2 rounded-md transition-wellness ${
                            viewMode === 'grid' ? 'bg-card shadow-wellness' : ''
                          }`}
                        >
                          <Icon name="Grid3X3" size={16} />
                        </button>
                        <button
                          onClick={() => setViewMode('list')}
                          className={`p-2 rounded-md transition-wellness ${
                            viewMode === 'list' ? 'bg-card shadow-wellness' : ''
                          }`}
                        >
                          <Icon name="List" size={16} />
                        </button>
                      </div>

                      {/* Sort Dropdown */}
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e?.target?.value)}
                        className="bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="featured">Featured</option>
                        <option value="newest">Newest</option>
                        <option value="price-low">Price: Low to High</option>
                        <option value="price-high">Price: High to Low</option>
                        <option value="rating">Highest Rated</option>
                      </select>
                    </div>
                  </div>

                  {/* Results Summary */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      Showing {sortedProducts?.length} of {products?.length} products
                    </span>
                    {filters?.search && (
                      <span>
                        Search results for "{filters?.search}"
                      </span>
                    )}
                  </div>
                </div>

                {/* Featured Offers */}
                <FeaturedOffers
                  offers={featuredOffers}
                  onOfferClick={handleOfferClick}
                  userTier={user?.tier}
                />

                <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                  {/* Products Grid */}
                  <div className="xl:col-span-3">
                    {sortedProducts?.length > 0 ? (
                      <div className={`grid gap-6 ${
                        viewMode === 'grid' ?'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :'grid-cols-1'
                      }`}>
                        {sortedProducts?.map((product) => (
                          <ProductCard
                            key={product?.id}
                            product={product}
                            onAddToWishlist={handleAddToWishlist}
                            onQuickView={handleProductClick}
                            onAddToCart={handleAddToCart}
                            userTier={user?.tier}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Icon name="Search" size={48} className="text-muted mx-auto mb-4" />
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                          No products found
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          Try adjusting your filters or search terms
                        </p>
                        <Button variant="outline" onClick={handleClearFilters}>
                          Clear Filters
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Sidebar Content */}
                  <div className="space-y-6">
                    <RecentlyViewed
                      products={recentlyViewed}
                      onProductClick={handleProductClick}
                      onClearHistory={() => console.log('Clear history')}
                    />
                    
                    <PersonalizedRecommendations
                      recommendations={recommendations}
                      onProductClick={handleProductClick}
                      onRefreshRecommendations={() => console.log('Refresh recommendations')}
                      userGoals={user?.goals}
                      userTier={user?.tier}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Quick View Modal */}
      <QuickViewModal
        product={selectedProduct}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        userTier={user?.tier}
      />
    </div>
  );
};

export default WellnessMarketplace;